// ============================================================
// Holiday Page — 國定假日管理
// 勞基法 §37 紀念日、勞動節日及其他應放假之日
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

interface Holiday {
  date: string;
  name: string;
  type: 'NATIONAL' | 'COMPANY' | 'OTHER';
  isHoliday: boolean;
}

interface GovHoliday { date: string; name: string; isHoliday: boolean }

interface ImportPreview {
  year: number;
  source: string;
  toAdd: GovHoliday[];
  lockedSkipped: GovHoliday[];
  notInGov: { date: string; name: string }[];
  unchangedCount: number;
}

interface HolidayFile {
  year: number;
  source?: string;
  note?: string;
  holidays: Holiday[];
}

const TYPE_LABEL: Record<string, { text: string; color: string; bg: string }> = {
  NATIONAL: { text: '國定', color: 'text-rose-600', bg: 'bg-rose-100' },
  COMPANY: { text: '公司', color: 'text-earth-600', bg: 'bg-earth-100' },
  OTHER: { text: '其他', color: 'text-morandi-600', bg: 'bg-morandi-100' },
};

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

function formatWeekday(dateStr: string): string {
  const d = new Date(dateStr);
  return WEEKDAY[d.getDay()];
}

export function HolidayPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<HolidayFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  // 從政府行事曆匯入：先預覽要新增哪幾天，老闆按確認才寫入
  const openImport = async () => {
    setImportBusy(true);
    try {
      setPreview(await api.get<ImportPreview>(`/holidays/import-gov/preview?year=${year}`));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setImportBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!preview) return;
    setImportBusy(true);
    try {
      const r = await api.post<{ added: number }>('/holidays/import-gov', { year: preview.year });
      setPreview(null);
      await fetchData();
      alert(`已匯入 ${r.added} 天。`);
    } catch (err) {
      alert('匯入失敗：' + (err as Error).message);
    } finally {
      setImportBusy(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<HolidayFile>(`/holidays?year=${year}`);
      setData(res);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (h: Holiday) => {
    await api.post('/holidays', h);
    setEditing(null);
    setShowAdd(false);
    fetchData();
  };

  const handleDelete = async (dateStr: string) => {
    if (!confirm(`確定刪除 ${dateStr} 的假日？`)) return;
    await api.delete(`/holidays/${dateStr}`);
    fetchData();
  };

  // 統計各類型
  const nationalCount = data?.holidays.filter(h => h.type === 'NATIONAL').length ?? 0;
  const companyCount = data?.holidays.filter(h => h.type === 'COMPANY').length ?? 0;
  const totalCount = data?.holidays.length ?? 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-morandi-800">行事曆管理</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setYear(y => y - 1)}
            className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center"
          >‹</button>
          <span className="text-lg font-semibold text-morandi-700 min-w-[80px] text-center">{year} 年</span>
          <button
            onClick={() => setYear(y => y + 1)}
            className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center"
          >›</button>
        </div>
      </div>

      {/* 摘要 */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="假日總數" value={totalCount} color="text-morandi-700" />
        <Stat label="國定假日" value={nationalCount} color="text-rose-500" />
        <Stat label="公司假日" value={companyCount} color="text-earth-500" />
      </div>

      {data?.source && (
        <div className="text-xs text-morandi-400 px-1">
          資料來源：{data.source}
          {data.note && <span className="ml-2 text-morandi-300">· {data.note}</span>}
        </div>
      )}

      {/* 新增按鈕 */}
      <div className="flex flex-wrap justify-end gap-2">
        <button
          onClick={openImport}
          disabled={importBusy}
          className="px-4 py-2 bg-white border border-earth-300 text-earth-600 hover:bg-earth-50 rounded-btn text-sm transition-colors disabled:opacity-50"
        >
          {importBusy && !preview ? '讀取政府行事曆…' : `從政府行事曆匯入 ${year} 年`}
        </button>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm transition-colors"
        >
          + 新增假日
        </button>
      </div>

      {/* 假日列表 */}
      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : data && data.holidays.length > 0 ? (
        <div className="card-morandi overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-morandi-50 text-morandi-500 text-xs">
                <th className="text-left px-4 py-3 font-medium">日期</th>
                <th className="text-left px-2 py-3 font-medium">星期</th>
                <th className="text-left px-2 py-3 font-medium">名稱</th>
                <th className="text-center px-2 py-3 font-medium">類型</th>
                <th className="text-center px-2 py-3 font-medium">狀態</th>
                <th className="text-right px-4 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.holidays.map((h) => {
                const cfg = TYPE_LABEL[h.type];
                const wd = formatWeekday(h.date);
                const isWeekend = wd === '六' || wd === '日';
                return (
                  <tr key={h.date} className="border-t border-morandi-100 hover:bg-morandi-50">
                    <td className="px-4 py-3 font-mono text-morandi-700">{h.date}</td>
                    <td className={`px-2 py-3 text-sm ${isWeekend ? 'text-rose-400' : 'text-morandi-500'}`}>{wd}</td>
                    <td className="px-2 py-3 text-morandi-700">{h.name}</td>
                    <td className="text-center px-2 py-3">
                      <span className={`badge text-xs ${cfg.bg} ${cfg.color}`}>{cfg.text}</span>
                    </td>
                    <td className="text-center px-2 py-3">
                      {h.isHoliday ? (
                        <span className="badge text-xs bg-sage-100 text-sage-600">放假</span>
                      ) : (
                        <span className="badge text-xs bg-morandi-100 text-morandi-500">調整上班</span>
                      )}
                    </td>
                    <td className="text-right px-4 py-3">
                      <button
                        onClick={() => setEditing(h)}
                        className="text-xs text-earth-500 hover:text-earth-700 mr-2"
                      >編輯</button>
                      <button
                        onClick={() => handleDelete(h.date)}
                        className="text-xs text-rose-400 hover:text-rose-600"
                      >刪除</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-10 text-morandi-300">本年度尚無假日資料</div>
      )}

      {/* 政府行事曆匯入預覽 */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div>
              <h2 className="text-lg font-bold text-morandi-800">{preview.year} 年政府行事曆</h2>
              <div className="text-xs text-morandi-400 mt-0.5">來源：{preview.source}</div>
            </div>

            {preview.toAdd.length > 0 ? (
              <div>
                <div className="text-sm font-semibold text-morandi-700 mb-1">會新增這 {preview.toAdd.length} 天</div>
                <div className="space-y-1">
                  {preview.toAdd.map(h => (
                    <div key={h.date} className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-morandi-600">{h.date}</span>
                      <span className="text-morandi-400 text-xs">({formatWeekday(h.date)})</span>
                      <span className="text-morandi-700">{h.name}</span>
                      {!h.isHoliday && <span className="badge text-xs bg-morandi-100 text-morandi-500">調整上班</span>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-sm text-sage-600">系統的假日已經和政府行事曆一致，不用匯入。</div>
            )}

            {preview.lockedSkipped.length > 0 && (
              <div className="text-xs text-morandi-500">
                <div className="font-medium mb-0.5">這幾天在已結算的月份，不會動（已發的薪水不變）：</div>
                {preview.lockedSkipped.map(h => <div key={h.date}>{h.date} {h.name}</div>)}
              </div>
            )}

            {preview.notInGov.length > 0 && (
              <div className="text-xs text-amber-600">
                <div className="font-medium mb-0.5">系統有、政府行事曆沒有（不會刪除，請自己判斷）：</div>
                {preview.notInGov.map(h => <div key={h.date}>{h.date} {h.name}</div>)}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setPreview(null)} className="px-4 py-2 text-sm text-morandi-500 hover:bg-morandi-100 rounded-btn">取消</button>
              {preview.toAdd.length > 0 && (
                <button onClick={confirmImport} disabled={importBusy}
                  className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn disabled:opacity-50">
                  {importBusy ? '匯入中…' : `匯入 ${preview.toAdd.length} 天`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 編輯/新增 Modal */}
      {(editing || showAdd) && (
        <HolidayModal
          initial={editing}
          defaultYear={year}
          onSave={handleSave}
          onClose={() => { setEditing(null); setShowAdd(false); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="card-morandi p-3 text-center">
      <div className={`text-2xl font-bold font-mono ${color}`}>{value}</div>
      <div className="text-xs text-morandi-400 mt-0.5">{label}</div>
    </div>
  );
}

function HolidayModal({ initial, defaultYear, onSave, onClose }: {
  initial: Holiday | null;
  defaultYear: number;
  onSave: (h: Holiday) => void;
  onClose: () => void;
}) {
  const [date, setDate] = useState(initial?.date ?? `${defaultYear}-01-01`);
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<'NATIONAL' | 'COMPANY' | 'OTHER'>(initial?.type ?? 'COMPANY');
  const [isHoliday, setIsHoliday] = useState(initial?.isHoliday ?? true);

  const isEditing = !!initial;

  const submit = () => {
    if (!name.trim()) { alert('請輸入假日名稱'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { alert('日期格式錯誤'); return; }
    onSave({ date, name: name.trim(), type, isHoliday });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-morandi-800">{isEditing ? '編輯假日' : '新增假日'}</h2>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">日期 *</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            disabled={isEditing}
            className="input-field"
          />
          {isEditing && <div className="text-xs text-morandi-300 mt-0.5">編輯時日期無法修改</div>}
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">名稱 *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="例：端午節、公司成立紀念日"
            className="input-field"
          />
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">類型</label>
          <div className="flex gap-2">
            {(['NATIONAL', 'COMPANY', 'OTHER'] as const).map(t => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`flex-1 px-3 py-2 rounded-btn text-sm transition-colors
                  ${type === t
                    ? 'bg-earth-400 text-white'
                    : 'bg-morandi-100 text-morandi-500 hover:bg-morandi-200'}`}
              >
                {TYPE_LABEL[t].text}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isHoliday}
              onChange={e => setIsHoliday(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm text-morandi-700">放假日</span>
            <span className="text-xs text-morandi-300 ml-1">（取消勾選表示調整為上班日）</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-morandi-500 hover:bg-morandi-100 rounded-btn"
          >取消</button>
          <button
            onClick={submit}
            className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn"
          >儲存</button>
        </div>
      </div>
    </div>
  );
}
